import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import RTLWrapper from "../components/RTLWrapper";

// Import the same words from WordLesson
const arabicWords = [
  // Greetings & Basic Phrases
  { word: "السلام عليكم", pronunciation: "As-salaamu alaykum", meaning: "Peace be upon you" },
  { word: "مرحباً", pronunciation: "Marhaban", meaning: "Hello" },
  { word: "كيف حالك؟", pronunciation: "Kayfa ḥaaluka/ḥaaluki", meaning: "How are you?" },
  { word: "نعم", pronunciation: "Naʿam", meaning: "Yes" },
  { word: "لا", pronunciation: "Laa", meaning: "No" },
  { word: "شكراً", pronunciation: "Shukran", meaning: "Thank you" },
  { word: "من فضلك", pronunciation: "Min faḍlik", meaning: "Please" },
  { word: "عفواً", pronunciation: "ʿAfwan", meaning: "You're welcome" },
  { word: "آسف", pronunciation: "Āsif", meaning: "Sorry" },
  { word: "مع السلامة", pronunciation: "Maʿa as-salāmah", meaning: "Goodbye" },
  
  // Family Members
  { word: "أمي", pronunciation: "Ummi", meaning: "My mother" },
  { word: "أبي", pronunciation: "Abi", meaning: "My father" },
  { word: "أخي", pronunciation: "Akhi", meaning: "My brother" },
  { word: "أختي", pronunciation: "Ukhti", meaning: "My sister" },
  { word: "جدّي", pronunciation: "Jaddi", meaning: "My grandfather" },
  { word: "جدتي", pronunciation: "Jaddati", meaning: "My grandmother" },
  { word: "عمي", pronunciation: "ʿAmmi", meaning: "My uncle" },
  { word: "خالي", pronunciation: "Khali", meaning: "My maternal uncle" },
  { word: "ابني", pronunciation: "Ibnī", meaning: "My son" },
  { word: "ابنتي", pronunciation: "Ibntī", meaning: "My daughter" },
  
  // Home & Objects
  { word: "بيت", pronunciation: "Bayt", meaning: "House" },
  { word: "كتاب", pronunciation: "Kitaab", meaning: "Book" },
  { word: "قلم", pronunciation: "Qalam", meaning: "Pen" },
  { word: "ورقة", pronunciation: "Waraqah", meaning: "Paper" },
  { word: "مكتب", pronunciation: "Maktab", meaning: "Desk" },
  { word: "كرسي", pronunciation: "Kursī", meaning: "Chair" },
  { word: "باب", pronunciation: "Bāb", meaning: "Door" },
  { word: "نافذة", pronunciation: "Nāfidhah", meaning: "Window" },
  { word: "مصباح", pronunciation: "Miṣbāḥ", meaning: "Lamp" },
  { word: "سرير", pronunciation: "Sarīr", meaning: "Bed" },
  { word: "طاولة", pronunciation: "Ṭāwilah", meaning: "Table" },
  { word: "مطبخ", pronunciation: "Maṭbakh", meaning: "Kitchen" },
  { word: "حمام", pronunciation: "Ḥammām", meaning: "Bathroom" },
  { word: "غرفة", pronunciation: "Ghurfah", meaning: "Room" },
  { word: "حديقة", pronunciation: "Ḥadīqah", meaning: "Garden" },
  
  // Animals
  { word: "قطة", pronunciation: "Qiṭṭa", meaning: "Cat" },
  { word: "كلب", pronunciation: "Kalb", meaning: "Dog" },
  { word: "حصان", pronunciation: "Ḥiṣān", meaning: "Horse" },
  { word: "بقرة", pronunciation: "Baqarah", meaning: "Cow" },
  { word: "خروف", pronunciation: "Kharūf", meaning: "Sheep" },
  { word: "دجاجة", pronunciation: "Dajājah", meaning: "Chicken" },
  { word: "أسد", pronunciation: "Asad", meaning: "Lion" },
  { word: "فيل", pronunciation: "Fīl", meaning: "Elephant" },
  { word: "نمر", pronunciation: "Nimr", meaning: "Tiger" },
  { word: "دب", pronunciation: "Dubb", meaning: "Bear" },
  { word: "أرنب", pronunciation: "Arnab", meaning: "Rabbit" },
  { word: "فأر", pronunciation: "Faʾr", meaning: "Mouse" },
  { word: "طائر", pronunciation: "Ṭāʾir", meaning: "Bird" },
  { word: "سمكة", pronunciation: "Samakah", meaning: "Fish" },
  { word: "ثعبان", pronunciation: "Thuʿbān", meaning: "Snake" },
  
  // Colors
  { word: "أحمر", pronunciation: "Aḥmar", meaning: "Red" },
  { word: "أزرق", pronunciation: "Azraq", meaning: "Blue" },
  { word: "أخضر", pronunciation: "Akhḍar", meaning: "Green" },
  { word: "أصفر", pronunciation: "Aṣfar", meaning: "Yellow" },
  { word: "أسود", pronunciation: "Aswad", meaning: "Black" },
  { word: "أبيض", pronunciation: "Abyaḍ", meaning: "White" },
  { word: "بني", pronunciation: "Bunnī", meaning: "Brown" },
  { word: "رمادي", pronunciation: "Ramādī", meaning: "Gray" },
  { word: "وردي", pronunciation: "Wardī", meaning: "Pink" },
  { word: "برتقالي", pronunciation: "Burtuqālī", meaning: "Orange" },
  { word: "بنفسجي", pronunciation: "Banafsajī", meaning: "Purple" },
  { word: "ذهبي", pronunciation: "Dhahabī", meaning: "Gold" },
  { word: "فضي", pronunciation: "Fiḍḍī", meaning: "Silver" },
  
  // Numbers
  { word: "واحد", pronunciation: "Waaḥid", meaning: "One" },
  { word: "اثنان", pronunciation: "Ithnaan", meaning: "Two" },
  { word: "ثلاثة", pronunciation: "Thalāthah", meaning: "Three" },
  { word: "أربعة", pronunciation: "Arbaʿah", meaning: "Four" },
  { word: "خمسة", pronunciation: "Khamsah", meaning: "Five" },
  { word: "ستة", pronunciation: "Sittah", meaning: "Six" },
  { word: "سبعة", pronunciation: "Sabʿah", meaning: "Seven" },
  { word: "ثمانية", pronunciation: "Thamāniyah", meaning: "Eight" },
  { word: "تسعة", pronunciation: "Tisʿah", meaning: "Nine" },
  { word: "عشرة", pronunciation: "ʿAsharah", meaning: "Ten" },
  
  // Food & Drinks
  { word: "خبز", pronunciation: "Khubz", meaning: "Bread" },
  { word: "لحم", pronunciation: "Laḥm", meaning: "Meat" },
  { word: "دجاج", pronunciation: "Dajāj", meaning: "Chicken" },
  { word: "سمك", pronunciation: "Samak", meaning: "Fish" },
  { word: "أرز", pronunciation: "Aruzz", meaning: "Rice" },
  { word: "خضار", pronunciation: "Khuḍār", meaning: "Vegetables" },
  { word: "فواكه", pronunciation: "Fawākih", meaning: "Fruits" },
  { word: "تفاح", pronunciation: "Tuffāḥ", meaning: "Apple" },
  { word: "موز", pronunciation: "Mawz", meaning: "Banana" },
  { word: "برتقال", pronunciation: "Burtuqāl", meaning: "Orange" },
  { word: "حليب", pronunciation: "Ḥalīb", meaning: "Milk" },
  { word: "ماء", pronunciation: "Māʾ", meaning: "Water" },
  { word: "شاي", pronunciation: "Shāy", meaning: "Tea" },
  { word: "قهوة", pronunciation: "Qahwah", meaning: "Coffee" },
  { word: "عسل", pronunciation: "ʿAsal", meaning: "Honey" },
  
  // Body Parts
  { word: "رأس", pronunciation: "Raʾs", meaning: "Head" },
  { word: "عين", pronunciation: "ʿAyn", meaning: "Eye" },
  { word: "أذن", pronunciation: "Udhun", meaning: "Ear" },
  { word: "أنف", pronunciation: "Anf", meaning: "Nose" },
  { word: "فم", pronunciation: "Fam", meaning: "Mouth" },
  { word: "يد", pronunciation: "Yad", meaning: "Hand" },
  { word: "رجل", pronunciation: "Rijl", meaning: "Foot" },
  { word: "قلب", pronunciation: "Qalb", meaning: "Heart" },
  { word: "ظهر", pronunciation: "Ẓahr", meaning: "Back" },
  { word: "بطن", pronunciation: "Baṭn", meaning: "Stomach" },
  
  // Clothing
  { word: "قميص", pronunciation: "Qamīṣ", meaning: "Shirt" },
  { word: "بنطلون", pronunciation: "Bantalūn", meaning: "Pants" },
  { word: "فستان", pronunciation: "Fustān", meaning: "Dress" },
  { word: "حذاء", pronunciation: "Ḥidhāʾ", meaning: "Shoe" },
  { word: "قبعة", pronunciation: "Qubʿah", meaning: "Hat" },
  { word: "جورب", pronunciation: "Jawrab", meaning: "Sock" },
  { word: "حزام", pronunciation: "Ḥizām", meaning: "Belt" },
  { word: "ساعة", pronunciation: "Sāʿah", meaning: "Watch" },
  
  // Weather & Nature
  { word: "شمس", pronunciation: "Shams", meaning: "Sun" },
  { word: "قمر", pronunciation: "Qamar", meaning: "Moon" },
  { word: "نجمة", pronunciation: "Najmah", meaning: "Star" },
  { word: "سماء", pronunciation: "Samāʾ", meaning: "Sky" },
  { word: "أرض", pronunciation: "Arḍ", meaning: "Earth" },
  { word: "بحر", pronunciation: "Baḥr", meaning: "Sea" },
  { word: "نهر", pronunciation: "Nahr", meaning: "River" },
  { word: "جبل", pronunciation: "Jabal", meaning: "Mountain" },
  { word: "شجرة", pronunciation: "Shajarah", meaning: "Tree" },
  { word: "زهرة", pronunciation: "Zahrah", meaning: "Flower" },
  { word: "مطر", pronunciation: "Maṭar", meaning: "Rain" },
  { word: "ثلج", pronunciation: "Thalj", meaning: "Snow" },
  { word: "ريح", pronunciation: "Rīḥ", meaning: "Wind" },
  { word: "سحاب", pronunciation: "Saḥāb", meaning: "Cloud" },
  
  // Time & Days
  { word: "يوم", pronunciation: "Yawm", meaning: "Day" },
  { word: "ليلة", pronunciation: "Laylah", meaning: "Night" },
  { word: "صباح", pronunciation: "Ṣabāḥ", meaning: "Morning" },
  { word: "مساء", pronunciation: "Masāʾ", meaning: "Evening" },
  { word: "ساعة", pronunciation: "Sāʿah", meaning: "Hour" },
  { word: "دقيقة", pronunciation: "Daqīqah", meaning: "Minute" },
  { word: "أسبوع", pronunciation: "Usbūʿ", meaning: "Week" },
  { word: "شهر", pronunciation: "Shahr", meaning: "Month" },
  { word: "سنة", pronunciation: "Sanah", meaning: "Year" },
  
  // Transportation
  { word: "سيارة", pronunciation: "Sayyārah", meaning: "Car" },
  { word: "حافلة", pronunciation: "Ḥāfilah", meaning: "Bus" },
  { word: "قطار", pronunciation: "Qiṭār", meaning: "Train" },
  { word: "طائرة", pronunciation: "Ṭāʾirah", meaning: "Airplane" },
  { word: "دراجة", pronunciation: "Darrājah", meaning: "Bicycle" },
  { word: "قارب", pronunciation: "Qārib", meaning: "Boat" },
  { word: "مطار", pronunciation: "Maṭār", meaning: "Airport" },
  { word: "محطة", pronunciation: "Maḥaṭṭah", meaning: "Station" },
  
  // School & Learning
  { word: "مدرسة", pronunciation: "Madrasah", meaning: "School" },
  { word: "معلم", pronunciation: "Muʿallim", meaning: "Teacher" },
  { word: "طالب", pronunciation: "Ṭālib", meaning: "Student" },
  { word: "صف", pronunciation: "Ṣaff", meaning: "Class" },
  { word: "امتحان", pronunciation: "Imtiḥān", meaning: "Exam" },
  { word: "واجب", pronunciation: "Wājib", meaning: "Homework" },
  { word: "مكتبة", pronunciation: "Maktabah", meaning: "Library" },
  { word: "سبورة", pronunciation: "Sabbūrah", meaning: "Blackboard" },
  
  // Emotions & Feelings
  { word: "سعيد", pronunciation: "Saʿīd", meaning: "Happy" },
  { word: "حزين", pronunciation: "Ḥazīn", meaning: "Sad" },
  { word: "غاضب", pronunciation: "Ghāḍib", meaning: "Angry" },
  { word: "خائف", pronunciation: "Khāʾif", meaning: "Afraid" },
  { word: "متفائل", pronunciation: "Mutafaʾil", meaning: "Optimistic" },
  { word: "متعب", pronunciation: "Mutaʿab", meaning: "Tired" },
  { word: "جائع", pronunciation: "Jāʾiʿ", meaning: "Hungry" },
  { word: "عطشان", pronunciation: "ʿAṭshān", meaning: "Thirsty" },
  { word: "مرض", pronunciation: "Mariḍ", meaning: "Sick" },
  { word: "صحيح", pronunciation: "Ṣaḥīḥ", meaning: "Healthy" }
];

function WordsQuiz() {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [gameFinished, setGameFinished] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [gameStarted, setGameStarted] = useState(false);

  // Generate random questions
  const generateQuestions = () => {
    const shuffled = [...arabicWords].sort(() => Math.random() - 0.5);
    const selectedWords = shuffled.slice(0, 10); // 10 questions
    
    const generatedQuestions = selectedWords.map(word => {
      // Get 3 random wrong answers
      const wrongAnswers = arabicWords
        .filter(w => w.meaning !== word.meaning)
        .sort(() => Math.random() - 0.5)
        .slice(0, 3)
        .map(w => w.meaning);
      
      // Combine correct and wrong answers and shuffle
      const allAnswers = [word.meaning, ...wrongAnswers].sort(() => Math.random() - 0.5);
      
      return {
        word: word.word,
        pronunciation: word.pronunciation,
        correctAnswer: word.meaning,
        answers: allAnswers
      };
    });
    
    setQuestions(generatedQuestions);
  };

  useEffect(() => {
    generateQuestions();
  }, []);

  const handleAnswerSelect = (answer) => {
    if (showResult) return;
    
    setSelectedAnswer(answer);
    setShowResult(true);
    
    if (answer === questions[currentQuestion].correctAnswer) {
      setScore(score + 1);
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowResult(false);
    } else {
      setGameFinished(true);
    }
  };

  const handleRestart = () => {
    setCurrentQuestion(0);
    setScore(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setGameFinished(false);
    setGameStarted(false);
    generateQuestions();
  };

  const startGame = () => {
    setGameStarted(true);
  };

  if (!gameStarted) {
    return (
      <RTLWrapper>
        <div className="p-4 sm:p-6 text-center min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 relative overflow-hidden">
          {/* Modern background elements */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(120,119,198,0.1),transparent_50%)]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(255,119,198,0.1),transparent_50%)]"></div>
          
          <div className="max-w-2xl mx-auto relative z-10">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 bg-clip-text text-transparent">
              🧠 Arabic Words Quiz
            </h1>
            
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/30 mb-8">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">Test Your Knowledge!</h2>
              <p className="text-gray-600 mb-6">
                You'll be shown Arabic words and need to choose the correct English meaning from 4 options.
              </p>
              <p className="text-sm text-gray-500 mb-8">
                📝 {questions.length} questions • 🎯 Multiple choice • ⏱️ Take your time
              </p>
              
              <button
                onClick={startGame}
                className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white text-xl font-bold px-8 py-4 rounded-2xl shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl"
              >
                🚀 Start Quiz
              </button>
            </div>
          </div>
        </div>
      </RTLWrapper>
    );
  }

  if (gameFinished) {
    const percentage = Math.round((score / questions.length) * 100);
    let message = "";
    let emoji = "";
    
    if (percentage >= 90) {
      message = "Excellent! You're an Arabic expert!";
      emoji = "🌟";
    } else if (percentage >= 70) {
      message = "Great job! You're doing well!";
      emoji = "👏";
    } else if (percentage >= 50) {
      message = "Good effort! Keep practicing!";
      emoji = "👍";
    } else {
      message = "Keep learning! Practice makes perfect!";
      emoji = "💪";
    }

    return (
      <RTLWrapper>
        <div className="p-4 sm:p-6 text-center min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 relative overflow-hidden">
          {/* Modern background elements */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(120,119,198,0.1),transparent_50%)]"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(255,119,198,0.1),transparent_50%)]"></div>
          
          <div className="max-w-2xl mx-auto relative z-10">
            <h1 className="text-4xl sm:text-5xl font-bold mb-8 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 bg-clip-text text-transparent">
              🎉 Quiz Complete!
            </h1>
            
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/30 mb-8">
              <div className="text-6xl mb-4">{emoji}</div>
              <h2 className="text-3xl font-bold text-gray-800 mb-4">{message}</h2>
              
              <div className="bg-gradient-to-r from-blue-100 to-purple-100 rounded-xl p-6 mb-6">
                <div className="text-4xl font-bold text-blue-600 mb-2">{score}/{questions.length}</div>
                <div className="text-2xl font-semibold text-gray-700">{percentage}%</div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={handleRestart}
                  className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white text-lg font-bold px-6 py-3 rounded-xl shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl"
                >
                  🔄 Play Again
                </button>
                <button
                  onClick={() => window.location.href = '/word-lesson'}
                  className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white text-lg font-bold px-6 py-3 rounded-xl shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl"
                >
                  📚 Study More
                </button>
              </div>
            </div>
          </div>
        </div>
      </RTLWrapper>
    );
  }

  const currentQ = questions[currentQuestion];

  return (
    <RTLWrapper>
      <div className="p-4 sm:p-6 text-center min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 relative overflow-hidden">
        {/* Modern background elements */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_80%,rgba(120,119,198,0.1),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(255,119,198,0.1),transparent_50%)]"></div>
        
        <div className="max-w-2xl mx-auto relative z-10">
          {/* Progress Bar */}
          <div className="w-full max-w-md mx-auto bg-white/60 backdrop-blur-sm rounded-full h-4 mb-6 overflow-hidden shadow-lg border border-white/30">
            <motion.div
              className="bg-gradient-to-r from-green-400 to-emerald-500 h-full rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
          
          <div className="text-sm text-gray-600 mb-8">
            Question {currentQuestion + 1} of {questions.length} • Score: {score}
          </div>

          {/* Question Card */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-white/30 mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold mb-6 bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 bg-clip-text text-transparent">
              What does this Arabic word mean?
            </h2>
            
            <div className="bg-gradient-to-r from-blue-100 to-purple-100 rounded-xl p-6 mb-8">
              <div className="text-4xl sm:text-5xl font-bold text-gray-800 mb-2" dir="rtl">
                {currentQ.word}
              </div>
              <div className="text-lg text-gray-600 italic">
                {currentQ.pronunciation}
              </div>
            </div>

            {/* Answer Options */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {currentQ.answers.map((answer, index) => {
                let buttonClass = "bg-white/80 backdrop-blur-sm text-gray-800 border border-gray-200 hover:bg-blue-50 hover:border-blue-300";
                
                if (showResult) {
                  if (answer === currentQ.correctAnswer) {
                    buttonClass = "bg-gradient-to-r from-green-400 to-emerald-500 text-white border border-green-300";
                  } else if (answer === selectedAnswer && answer !== currentQ.correctAnswer) {
                    buttonClass = "bg-gradient-to-r from-red-400 to-pink-500 text-white border border-red-300";
                  } else {
                    buttonClass = "bg-gray-100 text-gray-500 border border-gray-200";
                  }
                }

                return (
                  <button
                    key={index}
                    onClick={() => handleAnswerSelect(answer)}
                    className={`p-4 rounded-xl font-semibold text-lg transition-all duration-300 hover:scale-105 shadow-lg ${buttonClass}`}
                    disabled={showResult}
                  >
                    {answer}
                  </button>
                );
              })}
            </div>

            {/* Next Button */}
            {showResult && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="mt-6"
              >
                <button
                  onClick={handleNextQuestion}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-lg font-bold px-8 py-3 rounded-xl shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl"
                >
                  {currentQuestion < questions.length - 1 ? "Next Question ➡️" : "Finish Quiz 🏁"}
                </button>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </RTLWrapper>
  );
}

export default WordsQuiz;
